import { forwardRef, useRef } from 'react';
import { energy, msgOpacity, hexAlpha } from '../lib/energy';
import { TileMessage } from './Tile';

interface TickerRowProps {
  name: string;
  color: string;
  buffer: string;
  messages: TileMessage[];
  lastActivity: number | null;
  now: number;
  textScale?: number;
  maxLines?: number;
  isLocal?: boolean;
  velocity?: number;
  inputRef?: React.RefObject<HTMLInputElement>;
  onInput?: (text: string) => void;
  onSubmit?: () => void;
  onBadgeTap?: () => void;
}

export const TickerRow = forwardRef<HTMLDivElement, TickerRowProps>(
  function TickerRow({ name, color, buffer, messages, lastActivity, now, textScale = 1, maxLines = 1, isLocal, velocity = 0, inputRef, onInput, onSubmit, onBadgeTap }, ref) {
    const en = isLocal ? Math.max(0.4, energy(lastActivity, now)) : energy(lastActivity, now);
    const v = velocity;
    const isTyping = buffer.length > 0;
    const letter = (name[0] || '?').toUpperCase();
    const multiLine = maxLines > 1;

    // Track input sequence for transmission ripple (increments on every buffer change)
    const inputSeqRef = useRef(0);
    const prevBufRef = useRef(buffer);
    if (buffer !== prevBufRef.current) {
      inputSeqRef.current++;
      prevBufRef.current = buffer;
    }

    // Most recent visible message
    const visibleMessages = messages
      .filter(m => msgOpacity(m.submittedAt, now) > 0)
      .sort((a, b) => b.submittedAt - a.submittedAt);

    // Show buffer if typing, otherwise latest message
    const displayText = isTyping ? buffer : visibleMessages[0]?.text || '';
    const displayOpacity = isTyping
      ? 1
      : visibleMessages[0]
        ? msgOpacity(visibleMessages[0].submittedAt, now)
        : 0;

    return (
      <div
        ref={ref}
        className="pulse-ticker-row"
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: multiLine ? 'flex-start' : 'center',
          gap: 8,
          padding: multiLine ? '8px 10px' : '5px 10px',
          borderRadius: 6,
          background: en > 0.15
            ? `linear-gradient(90deg, ${hexAlpha(color, en * 18 + v * 12)} 0%, rgba(10,10,16,0.4) 100%)`
            : 'rgba(13,13,20,0.3)',
          border: `1px solid ${hexAlpha(color, en * 50 + 15 + v * 30)}`,
          opacity: Math.max(0.45, en * 0.35 + 0.55),
          transition: 'opacity 1s ease, background 0.8s ease, border-color 0.8s ease',
          minHeight: multiLine ? 40 : 30,
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        {/* Color letter badge */}
        <span
          onClick={isLocal && onBadgeTap ? (e) => { e.stopPropagation(); onBadgeTap(); } : undefined}
          style={{
            fontFamily: '"Space Mono", monospace',
            fontSize: 12 * textScale,
            fontWeight: 700,
            color: '#07070d',
            backgroundColor: color,
            width: 20 * textScale,
            height: 20 * textScale,
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: en > 0.3 ? `0 0 ${8 + v * 6}px ${hexAlpha(color, 100 + v * 55)}` : 'none',
            transition: 'box-shadow 0.8s ease',
            cursor: isLocal ? 'pointer' : 'default',
            marginTop: multiLine ? 2 : 0,
          }}
        >
          {letter}
        </span>

        {isLocal ? (
          /* Local user: live input, cursor pinned right like remote text */
          <div
            style={{
              flex: 1,
              overflow: 'hidden',
              maskImage: 'linear-gradient(90deg, transparent 0%, black 15%)',
              WebkitMaskImage: 'linear-gradient(90deg, transparent 0%, black 15%)',
            }}
          >
            <input
              ref={inputRef}
              value={buffer}
              onChange={(e) => onInput?.(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onSubmit?.(); } }}
              placeholder="type something..."
              autoFocus
              style={{
                width: '100%',
                background: 'none',
                border: 'none',
                outline: 'none',
                fontFamily: '"Space Mono", monospace',
                fontSize: 12 * textScale,
                color: '#d5d1c8',
                letterSpacing: 0.3,
                caretColor: color,
                textAlign: 'right',
                direction: 'ltr',
              }}
            />
          </div>
        ) : multiLine ? (
          /* Remote user: multi-line wrapped text (desktop) */
          <div
            style={{
              flex: 1,
              overflow: 'hidden',
              fontFamily: '"Space Mono", monospace',
              fontSize: 12 * textScale,
              color: '#d5d1c8',
              letterSpacing: 0.3,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              textAlign: 'right',
              lineHeight: 1.5,
              maxHeight: Math.round(12 * textScale * 1.5 * maxLines),
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              opacity: displayText ? displayOpacity : 0.2,
              transition: 'opacity 0.5s ease',
            }}
          >
            <span>
              {displayText || '\u00A0'}
              {isTyping && (
                <span
                  style={{
                    display: 'inline-block',
                    width: 5,
                    height: 5,
                    marginLeft: 4,
                    backgroundColor: color,
                    animation: 'breathe 2s ease-in-out infinite',
                    verticalAlign: 'middle',
                    borderRadius: '50%',
                  }}
                />
              )}
            </span>
          </div>
        ) : (
          /* Remote user: RTL container keeps newest text visible, overflow clips left */
          <div
            style={{
              flex: 1,
              overflow: 'hidden',
              direction: 'rtl',
              maskImage: 'linear-gradient(90deg, transparent 0%, black 15%)',
              WebkitMaskImage: 'linear-gradient(90deg, transparent 0%, black 15%)',
            }}
          >
            <div
              className="pulse-ticker-text"
              style={{
                whiteSpace: 'nowrap',
                direction: 'ltr',
                display: 'inline-block',
                fontFamily: '"Space Mono", monospace',
                fontSize: 12 * textScale,
                color: '#d5d1c8',
                letterSpacing: 0.3,
                opacity: displayText ? displayOpacity : 0.2,
                transition: 'opacity 0.5s ease',
              }}
            >
              {isTyping && displayText.length > 0 ? (
                <>
                  {displayText.slice(0, -1)}
                  <span key={displayText.length} className="pulse-ticker-char-new">
                    {displayText.slice(-1)}
                  </span>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 5,
                      height: 5,
                      marginLeft: 4,
                      backgroundColor: color,
                      animation: 'breathe 2s ease-in-out infinite',
                      verticalAlign: 'middle',
                      borderRadius: '50%',
                    }}
                  />
                </>
              ) : (
                displayText || '\u00A0'
              )}
            </div>
          </div>
        )}

        {/* Transmission ripple — brief inner glow per keystroke on local user */}
        {isLocal && inputSeqRef.current > 0 && (
          <div
            key={`tx-${inputSeqRef.current}`}
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: 6,
              boxShadow: `inset 0 0 0 1.5px ${hexAlpha(color, 70)}, inset 0 0 12px ${hexAlpha(color, 20)}`,
              animation: 'transmit 300ms ease-out forwards',
              pointerEvents: 'none',
            }}
          />
        )}
      </div>
    );
  }
);
